import isEmail from 'validator/lib/isEmail'
import { differenceInMinutes } from 'date-fns'
import { DONT_RENDER_APP, APP_ID_PRISMA_ACCESS_EDITION_ONPREM, APP_ID_PRISMA_ACCESS_EDITION, APP_ID_PRISMA_ACCESS, APP_ID_LOGGING_SERVICE } from '../constants/GlobalConstants'
import {
  ERROR_PERSONAL_EMAIL,
  ERROR_NO_CSP_ACCESS,
  ERROR_NO_CDL_INSTANCE,
  ERROR_NO_RESTRICTED_CSP_ACCESS,
  ERROR_NO_TSG_INSTANCE_CSP_ACCESS,
  ERROR_NO_CDL_INSTANCE_IN_RESTRICTED_ACCOUNTS,
  ERROR_NO_CDL_CSP_ACCESS
} from '../constants/LabelConstants'
import freeEmailDomains from 'free-email-domains'
import StringTemplate from './StringTemplate'
import { getVarValue } from './Sparky'
import { FF_FEDRAMP_HIGH, FF_ENABLE_PROFILE_ACTIVATION, VMFLEX_ENABLED_SUPPORT_ACCOUNT_ID } from './FeatureFlags'
import { isNonemptyArray } from '../../hub/utils/common'
export * from '../../hub/utils/common'

export const uniq = arr => (!arr || !arr.length ? [] : Array.from(new Set(Array.isArray(arr) ? arr : Array.from(arr))))

export const getCOBUsage = (tenantId, instances) => {
  const usage = uniq(instances.filter(inst =>
    Array.isArray(inst.associations) && inst.associations.some(asso => asso.tenant_id === tenantId)
  ).map(inst => ({ tenant_id: inst.tenant_id, app_id: inst.app_id })))
  return usage
}

export const getPlatformRegionItems = (activationAppId, apps, tsgInstances, support_account_id) => {
  const activationApp = apps[activationAppId]
  const cdlApp = apps[APP_ID_LOGGING_SERVICE]
  const platformRegionMap = new Map(apps['platform']?.regions?.map(region => [region.name, region]))

  const cdlInstance = tsgInstances.find(instance => instance.app_id === APP_ID_LOGGING_SERVICE)
  if (cdlInstance) {
    return [{ value: cdlApp?.regions.find(region => region?.name === cdlInstance.region)?.display, id: cdlInstance.region }]
  }

  const instancePlatformRegion = tsgInstances.find(instance => instance.platform_region)?.platform_region
  if (instancePlatformRegion) {
    return [{ value: cdlApp?.regions.find(region => region?.name === instancePlatformRegion)?.display, id: instancePlatformRegion }]
  }

  let items = []

  activationApp?.regions?.forEach(appRegion => {
    if (platformRegionMap.has(appRegion.name)) {
      const isRestrictedRegion = appRegion?.restricted_accounts
      if (!isRestrictedRegion) {
        items.push(appRegion)
      }
      else {
        if (appRegion.restricted_accounts.includes(support_account_id)) {
          items.push(appRegion)
        }
      }
    }
  })

  items = items.map(({ display, name }) => ({ id: name, value: display }))
  return items?.length ? items : [{ value: '' }]
}

export const getTSGInstancesBasedSupportAccount = ({ tsgInstances, supportAccounts, app, reMapKeys = true, allowFederal = false }) => {
  const fedHighEnv = getVarValue(FF_FEDRAMP_HIGH, false)
  const emptyAcc = [{ value: '' }]
  if (!Array.isArray(supportAccounts) || supportAccounts.length === 0) {
    throw new Error(ERROR_NO_CSP_ACCESS)
  }
  const data = isNonemptyArray(app?.restricted_accounts) ? filterWithRestrictedAccounts(supportAccounts, app?.restricted_accounts) : supportAccounts
  if (!data) {
    throw new Error(ERROR_NO_RESTRICTED_CSP_ACCESS)
  }
  let _filteredAccounts
  if (fedHighEnv || allowFederal) {
    _filteredAccounts = data
  }
  else {
    _filteredAccounts = data?.filter(account => !+account.is_federal)
  }
  const filteredAccounts = reMapKeys ? _filteredAccounts?.map(({ accountid, name }) => ({
    value: `${name} - ${accountid}`,
    description: name,
    id: accountid,
    children: `${name} - ${accountid}`,
  })) : _filteredAccounts

  if (tsgInstances.length === 0) {
    return filteredAccounts
  }
  const isCDLRequired = Boolean(app?.associations?.find(asso => asso.app_id === APP_ID_LOGGING_SERVICE && asso.required === true))
  const cdlInstance = tsgInstances.find(instance => instance.app_id === APP_ID_LOGGING_SERVICE)
  const cdlSupportAcc = cdlInstance ? filteredAccounts?.find(supportAcc => supportAcc.id === +cdlInstance.support_account_id) : null
  if (!cdlInstance && isCDLRequired && !app?.setup?.extra?.migrate_cdl) {
    throw new Error(ERROR_NO_CDL_INSTANCE)
  }
  if (cdlInstance && !cdlSupportAcc) {
    if (app?.restricted_accounts) {
      throw new Error(ERROR_NO_CDL_INSTANCE_IN_RESTRICTED_ACCOUNTS)
    }
    else {
      throw new Error(StringTemplate(ERROR_NO_CDL_CSP_ACCESS, [`${cdlInstance.support_account_id}`]))
    }
  }
  const tsgInstance = tsgInstances.find(instnace => instnace.app_id !== APP_ID_LOGGING_SERVICE)
  const tsgSupportAcc = tsgInstance ? filteredAccounts.find(supportAcc => supportAcc.id === +tsgInstance.support_account_id) : null
  if (tsgInstance && !tsgSupportAcc) {
    throw new Error(StringTemplate(ERROR_NO_TSG_INSTANCE_CSP_ACCESS, [`${tsgInstance.support_account_name} - ${tsgInstance.support_account_id}`]))
  }

  return cdlSupportAcc ? [cdlSupportAcc] : tsgSupportAcc ? [tsgSupportAcc] : emptyAcc
}

/**
 * Created by rathompson on 10/5/21
 * File: Utils
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

export const filterOutDoNotRenderApp = apps => apps?.filter(each => !DONT_RENDER_APP.includes(each.app_id))

/**
 * This util will help to transfer all the entitlements to prisma_access from prisma_access_edition
 * @param {*} apps
 * @returns new Array
 */
export const transferLicenseToPA = apps => {
  const licenseInstance = apps?.find(each => each.app_id === APP_ID_PRISMA_ACCESS_EDITION_ONPREM || each.app_id === APP_ID_PRISMA_ACCESS_EDITION) || {}
  const prismaAccess = apps?.find(each => each.app_id === APP_ID_PRISMA_ACCESS) || {}
  prismaAccess.contract = licenseInstance?.serial_number
  // prismaAccess.url is correct, do not overwrite here
  if (licenseInstance.provisioning_status === 'complete' && licenseInstance.status === 'running' &&
    prismaAccess.provisioning_status === 'complete' && prismaAccess.status === 'running') {
    prismaAccess.provisioning_status = 'complete'
  }
  else if (licenseInstance.provisioning_status === 'error' || prismaAccess.provisioning_status === 'error' ||
    licenseInstance.status === 'error' || prismaAccess.status === 'error') {
    prismaAccess.provisioning_status = 'error'
  }
  else {
    prismaAccess.provisioning_status = prismaAccess.provisioning_status ?? licenseInstance.provisioning_status
  }
  if (OBJECT.hasObject(prismaAccess)) {
    prismaAccess.entitlements = [...licenseInstance.entitlements || []]
  }
  return filterOutDoNotRenderApp(apps)
}
export const transferChildLicenseToPA = (apps) => {
  if (apps && apps.length > 0) {
    apps.forEach((each) => {
      if (each.instances) {
        each.instances = transferLicenseToPA(each.instances)
      }
    })
  }
  return apps
}
export const ACCOUNT_NAME_REGEX = /^[^\s\t]+[a-zA-Z0-9-_.]{0,255}$/
/***
 * This util tests string values against our email regex
 * @param email
 * @returns {boolean}
 */
export const validateEmail = email => isEmail(email)

export const checkPersonalEmail = email => {
  const emailDomain = email.split('@')[1]
  for (const domain of freeEmailDomains) {
    if (emailDomain.toLowerCase() === domain.toLowerCase()) {
      throw new Error(StringTemplate(ERROR_PERSONAL_EMAIL, [email]))
    }
  }
}

/***
 * General Array Utils
 */
export const ARRAY = {
  hasArray: array => !!(array && Array.isArray(array) && array.length),
  getLength: array => (ARRAY.hasArray(array) ? array.length : 0),
  clean: array => (ARRAY.hasArray(array) ? array.filter(Boolean) : [])
}

/***
 * General Object Utils
 */
export const OBJECT = {
  isObject: object => !!(object && !Array.isArray(object) && typeof object === 'object'),
  hasObject: object => !!(OBJECT.isObject(object) && Object.keys(object).length),
  reduce: object => (OBJECT.hasObject(object) ? Object.fromEntries(Object.entries(object).filter(([_, v]) => v)) : {}),
  deepFreeze: obj => {
    Object.keys(obj).forEach(prop => {
      if (typeof obj[prop] === 'object' && !Object.isFrozen(obj[prop])) OBJECT.deepFreeze(obj[prop])
    })
    return Object.freeze(obj)
  }
}

export const indexArray = (arr, key, { clone, cached = true } = {}) => {
  if (cached && Array.isArray(arr) && arr._map && arr.get) {
    return arr
  }
  const array = Array.isArray(arr) ? arr : []
  const reIndex = () => indexArray(array, key, { clone: false, cached: false })
  if (!ARRAY.hasArray(array)) {
    return Object.defineProperties(array, {
      _map: { configurable: true, value: new Map() },
      has: { configurable: true, value: () => false },
      get: { configurable: true, value: () => undefined },
      reIndex: { configurable: true, value: reIndex },
    })
  }
  const isFn = typeof key === 'function'
  const map = new Map(array.map(a => [isFn ? key(a, array) : a[key], a]))
  return Object.defineProperties(clone ? [...array] : array, {
    _map: { configurable: true, value: map },
    has: { configurable: true, value: id => map.has(id) },
    get: { configurable: true, value: id => map.get(id) },
    reIndex: { configurable: true, value: reIndex },
  })
}

// export const isNonemptyArray = (arr) => Array.isArray(arr) && arr.length > 0

/**
 *
 * @param {array} supportAccounts
 * @param {array} restrictedAccounts
 */
export const filterWithRestrictedAccounts = (supportAccounts, restrictedAccounts) => {
  return supportAccounts.filter(each => restrictedAccounts.includes(+each?.accountid || +each?.id))
}

export const isAccountAllowedForDAS = (apps = {}, user = {}) => {
  const isDASEnabled = Object.values(apps).some(appInfo => appInfo?.device_association)
  if (!isDASEnabled) {
    return false
  }
  return true // default enable
}

export const getSignature = (accountId, csps) => {
  const cspArray = ARRAY.clean(csps)
  const csp = cspArray.find(({ accountid }) => +accountid === +accountId)
  return csp?.signature || ''
}

export const getDasAppDisplay = (instance, apps, getAppName) => {
  const dasAppOverride = Object.values(apps)?.find(app => app.app_id === instance?.app_id)?.device_association?.app_override
  if (dasAppOverride) {
    return {
      appName: dasAppOverride?.display_name,
      sub_title: dasAppOverride?.sub_title
    }
  }
  return {
    appName: getAppName(instance?.app_id_display_override || instance?.app_id)
  }
}

const getISODateString = (date, defaults) => {
  const dateObj = date && new Date(date)
  return dateObj && +dateObj >= 0 ? dateObj.toISOString() : (defaults === true ? date : defaults)
}

export const isExpiredDate = (licenseDateString) => {
  return (differenceInMinutes(new Date(getISODateString(licenseDateString, true)), new Date()) <= 0)
}
export const isVMFlexActivationEnabled = (userAccounts) => {
  const userAccountsMap = new Set(userAccounts?.map(acc => String(acc.accountid)))
  const ff = Boolean(getVarValue(FF_ENABLE_PROFILE_ACTIVATION))
  if (!ff) {
    return false
  }
  const enabledAccount = JSON.parse(getVarValue(VMFLEX_ENABLED_SUPPORT_ACCOUNT_ID)) || []
  if (!enabledAccount?.length) {
    return true
  }
  return enabledAccount?.some(acc => userAccountsMap.has(acc))
}
